import { useFloating } from "@floating-ui/react-dom-interactions"
import type { Editor } from "@tiptap/core"
import { type CSSProperties, useEffect, useRef, useState } from "react"

import { TableEvent } from "../TableEvent"
import { TableColumnMenu } from "./TableColumnMenu"
import { TableRowMenu } from "./TableRowMenu"

interface TableHandlesMenusProps {
  editor: Editor
}

export function TableFloatingMenus(props: TableHandlesMenusProps) {
  return (
    <>
      <TableRowFloatingMenu {...props} />
      <TableColumnFloatingMenu {...props} />
    </>
  )
}

function TableRowFloatingMenu(props: TableHandlesMenusProps) {
  const { editor } = props
  const { triggerElementRef, open, onOpenChange, floatingStyle, floatingRef } =
    useFloatingMenu("row", editor)

  return (
    <TableRowMenu
      open={open}
      editor={editor}
      triggerRef={floatingRef}
      targetTriggerElementRef={triggerElementRef}
      onOpenChange={onOpenChange}
      floatingStyle={floatingStyle}
    />
  )
}

function TableColumnFloatingMenu(props: TableHandlesMenusProps) {
  const { editor } = props
  const { triggerElementRef, open, onOpenChange, floatingStyle, floatingRef } =
    useFloatingMenu("column", editor)

  return (
    <TableColumnMenu
      open={open}
      editor={editor}
      triggerRef={floatingRef}
      targetTriggerElementRef={triggerElementRef}
      onOpenChange={onOpenChange}
      floatingStyle={floatingStyle}
    />
  )
}

/**
 * Positions the table handle floating menus.
 *
 * Because our table row/column handles are positioned inside the table, and
 * rendered using DOM APIs and not React, we use a dummy trigger element to
 * be able to open the radix dialog, and set the trigger position using
 * floating-ui to be the same as our handles. The handle reference is passed by
 * a custom click event that this hook listens to, and opens the menu.
 *
 * TODO: We should try to update floating-ui and simplify this.
 * Ticket: https://linear.app/prismic/issue/DT-2625
 * This approach is based on SlashCommands.tsx, but it's a bit outdated and
 * unnecessarily complex.
 *
 */
function useFloatingMenu(type: "row" | "column", editor: Editor) {
  const triggerElementRef = useRef<HTMLElement>()
  const [open, onOpenChange] = useState(false)

  const { strategy, floating, reference, x, y } = useFloating({
    open,
    onOpenChange,
    placement: type === "column" ? "bottom-start" : "right-start",
    strategy: "fixed",
  })

  useEffect(() => {
    // Listen to table handle click events and open the menu.
    return TableEvent.listen("handleClick", {
      editor,
      callback: (detail) => {
        if (detail.type !== type) return
        reference(detail.referenceElement)
        triggerElementRef.current = detail.referenceElement
        onOpenChange(true)
      },
    })
  }, [editor, reference, type])

  return {
    open,
    onOpenChange,
    triggerElementRef,
    floatingRef: floating,
    floatingStyle: {
      top: y ?? "",
      left: x ?? "",
      position: strategy,
      display: open ? "flex" : "none",
    } satisfies CSSProperties,
  }
}
